/**
 * SalMakes — Regroupement visuel des colliers dans le panier
 * ------------------------------------------------------------
 * Le configurateur ajoute un collier au panier comme PLUSIEURS lignes
 * Shopify natives (1 chaîne + N charms), chacune avec son propre prix et
 * son propre suivi de stock — c'est ce qui garantit un prix et un stock
 * 100% exacts sans app tierce ni Shopify Plus.
 *
 * Ce script n'a qu'un rôle cosmétique : sur la page panier et dans le
 * tiroir panier, il repère les lignes qui partagent la même propriété
 * cachée `_bundle_id` et insère au-dessus un bandeau "Ton collier
 * personnalisé" avec le sous-total et un bouton "Retirer le collier".
 *
 * Écrit pour la structure par défaut du thème Dawn (lignes marquées
 * `data-line="N"`). Si tu utilises un autre thème Online Store 2.0,
 * vérifie l'attribut qui identifie chaque ligne panier dans son code et
 * ajuste la constante LINE_SELECTOR ci-dessous si besoin — voir le guide
 * d'installation.
 */
(function () {
  'use strict';

  var LINE_SELECTOR = '[data-line]';
  var GROUP_CLASS = 'sm-bundle-row';
  var HEADER_CLASS = 'sm-bundle-header';
  var currencyFormatter = null;

  function formatMoney(cents, currencyCode) {
    try {
      if (!currencyFormatter || currencyFormatter.currency !== currencyCode) {
        currencyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'fr-MA', {
          style: 'currency',
          currency: currencyCode
        });
        currencyFormatter.currency = currencyCode;
      }
      return currencyFormatter.format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + currencyCode;
    }
  }

  function findLineRows() {
    var rows = Array.prototype.slice.call(document.querySelectorAll(LINE_SELECTOR));
    return rows.filter(function (row) {
      var line = parseInt(row.getAttribute('data-line'), 10);
      return !isNaN(line) && line > 0;
    });
  }

  function clearExistingHeaders(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('.' + HEADER_CLASS), function (el) {
      el.remove();
    });
    Array.prototype.forEach.call(scope.querySelectorAll('.' + GROUP_CLASS), function (el) {
      el.classList.remove(GROUP_CLASS);
    });
  }

  function buildHeaderContent(bundle, currencyCode) {
    var wrap = document.createElement('div');
    wrap.className = 'sm-bundle-header__inner';

    var title = document.createElement('strong');
    title.className = 'sm-bundle-header__title';
    title.textContent = bundle.name || 'Collier personnalisé SalMakes';
    wrap.appendChild(title);

    var meta = document.createElement('span');
    meta.className = 'sm-bundle-header__meta';
    meta.textContent = '1 chaîne + ' + bundle.charmCount + ' charm' + (bundle.charmCount > 1 ? 's' : '') + ' · ' + formatMoney(bundle.total, currencyCode);
    wrap.appendChild(meta);

    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'sm-bundle-header__remove';
    removeBtn.textContent = 'Retirer le collier';
    removeBtn.addEventListener('click', function () {
      removeBundle(bundle);
    });
    wrap.appendChild(removeBtn);

    return wrap;
  }

  function insertHeaderBeforeRow(row, bundle, currencyCode) {
    var isTableRow = row.tagName === 'TR';
    var header;

    if (isTableRow) {
      header = document.createElement('tr');
      header.className = HEADER_CLASS;
      var td = document.createElement('td');
      td.colSpan = row.cells ? row.cells.length : 6;
      td.appendChild(buildHeaderContent(bundle, currencyCode));
      header.appendChild(td);
    } else {
      header = document.createElement('div');
      header.className = HEADER_CLASS;
      header.appendChild(buildHeaderContent(bundle, currencyCode));
    }

    row.parentNode.insertBefore(header, row);
  }

  function removeBundle(bundle) {
    var updates = {};
    bundle.lines.forEach(function (line) {
      updates[line] = 0;
    });

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('salmakes:cart-bundle-removed', { detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

        // Meilleur-effort : si le thème expose un composant <cart-drawer>
        // avec renderContents (cas de Dawn), on l'utilise pour resynchroniser
        // l'affichage sans recharger la page.
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(cart);
        } else if (window.location.pathname.indexOf('/cart') === 0) {
          window.location.reload();
        } else {
          // Repli simple : on retire les lignes du collier de l'affichage
          // en attendant le prochain rafraîchissement complet du panier.
          groupAndRender();
        }
      })
      .catch(function (err) {
        console.error('[SalMakes] Impossible de retirer le collier du panier', err);
      });
  }

  function groupAndRender() {
    var rows = findLineRows();
    if (!rows.length) return;

    // Le tiroir panier et la page /cart peuvent toutes les deux être
    // présentes dans le DOM en même temps (Dawn garde le tiroir dans le
    // DOM même fermé). Chacune a sa propre liste de lignes 1..N pour le
    // MÊME panier : on traite donc chaque conteneur séparément plutôt que
    // de mélanger toutes les lignes trouvées sur la page.
    var containers = [];
    rows.forEach(function (row) {
      if (containers.indexOf(row.parentNode) === -1) {
        containers.push(row.parentNode);
      }
    });

    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        // On calcule les groupes une seule fois à partir de cart.js
        // (source de vérité pour les propriétés et les prix)...
        var bundles = {};
        cart.items.forEach(function (item, idx) {
          var lineNumber = idx + 1;
          var props = item.properties || {};
          var bundleId = props._bundle_id;
          if (!bundleId) return;

          if (!bundles[bundleId]) {
            bundles[bundleId] = { id: bundleId, name: props._bundle_name, total: 0, charmCount: 0, lines: [], firstLine: lineNumber };
          }
          bundles[bundleId].total += item.final_line_price;
          bundles[bundleId].lines.push(lineNumber);
          if (props._bundle_role && props._bundle_role.toLowerCase().indexOf('charm') !== -1) {
            bundles[bundleId].charmCount += 1;
          }
          bundles[bundleId].firstLine = Math.min(bundles[bundleId].firstLine, lineNumber);
        });

        var validBundles = Object.keys(bundles)
          .map(function (id) { return bundles[id]; })
          .filter(function (b) { return b.lines.length > 1; });

        if (!validBundles.length) return;

        // ...puis on applique ces groupes à CHAQUE conteneur trouvé
        // (page panier, tiroir panier, etc.) indépendamment.
        containers.forEach(function (container) {
          clearExistingHeaders(container);
          var rowsInContainer = rows.filter(function (r) { return r.parentNode === container; });

          validBundles.forEach(function (bundle) {
            var firstRow = rowsInContainer.find(function (r) {
              return parseInt(r.getAttribute('data-line'), 10) === bundle.firstLine;
            });
            if (!firstRow) return;

            insertHeaderBeforeRow(firstRow, bundle, cart.currency);

            bundle.lines.forEach(function (lineNumber) {
              var row = rowsInContainer.find(function (r) {
                return parseInt(r.getAttribute('data-line'), 10) === lineNumber;
              });
              if (row) row.classList.add(GROUP_CLASS);
            });
          });
        });
      })
      .catch(function (err) {
        console.error('[SalMakes] Regroupement du panier impossible', err);
      });
  }

  function debounce(fn, delay) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, delay);
    };
  }

  var debouncedRun = debounce(groupAndRender, 150);

  // Premier rendu.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', groupAndRender);
  } else {
    groupAndRender();
  }

  // Ré-exécution best-effort quand le panier est modifié ailleurs sur le
  // site (ajout, quantité changée, tiroir panier ouvert...).
  ['cart:refresh', 'cart:update', 'cart:build', 'salmakes:necklace-added'].forEach(function (evtName) {
    document.addEventListener(evtName, debouncedRun);
  });

  // Filet de sécurité : si le tiroir/la page panier est re-rendu par le
  // thème sans émettre d'événement connu, on détecte le changement du DOM
  // sur chaque conteneur de lignes trouvé (page panier ET tiroir panier).
  if (window.MutationObserver) {
    var observedParents = [];
    findLineRows().forEach(function (row) {
      if (row.parentNode && observedParents.indexOf(row.parentNode) === -1) {
        observedParents.push(row.parentNode);
      }
    });
    observedParents.forEach(function (parent) {
      var observer = new MutationObserver(debouncedRun);
      observer.observe(parent, { childList: true, subtree: false });
    });
  }
})();